import { useEffect, useMemo, useState } from "react";
import { api, fmtError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Search, Download, ChevronDown } from "lucide-react";
import DocumentDownloader from '../components/DocumentDownloader';

// Helper Popover component for the table row
const DownloadPopover = ({ rollNo, semesters }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedSem, setSelectedSem] = useState(semesters[0] || "I");

  return (
    <div className="relative">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-sm hover:bg-indigo-100 transition-colors"
      >
        <Download className="w-3.5 h-3.5" />
        Download GS
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 z-50 mt-2 w-64 p-3 bg-white border border-stone-200 rounded-sm shadow-xl">
          <div className="mb-3">
            <label className="block text-[10px] font-bold uppercase tracking-wider text-stone-500 mb-1">
              Select Semester
            </label>
            <select 
              value={selectedSem}
              onChange={(e) => setSelectedSem(e.target.value)}
              className="w-full text-sm font-mono p-1.5 border border-stone-200 rounded-sm"
            >
              {semesters.map(sm => (
                <option key={sm} value={sm}>Semester {sm}</option>
              ))}
            </select>
          </div>
          
          <DocumentDownloader 
            documentType="Grade Sheet"
            fileName={`GS_${rollNo}_Sem_${selectedSem}.pdf`}
            baseUrl={`/api/admin/student/${rollNo}/gs/${selectedSem}`}
          />
        </div>
      )}
      
      {/* Click-away overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default function AdminStudents() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  // standard roman numeral array for the dropdown choices
  const ALL_SEMESTERS = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII"];

  useEffect(() => {
    api
      .get("/admin/students")
      .then((r) => setStudents(r.data.students))
      .catch((e) => toast.error(fmtError(e)))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return students;
    return students.filter(
      (s) =>
        (s.roll_no || "").toLowerCase().includes(t) ||
        (s.name || "").toLowerCase().includes(t) ||
        (s.branch || "").toLowerCase().includes(t)
    );
  }, [q, students]);

  return (
    <div className="p-10 fade-up">
      <p className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold">
        Master register
      </p>
      <h1 className="font-display text-4xl mt-1">Students</h1>
      <p className="text-stone-600 text-sm mt-2">
        Roll numbers populated automatically from uploaded TC / GS PDFs. Use the
        Quick GS column to export a single-student starred grade sheet for any
        semester on file.
      </p>

      <div className="relative mt-8 max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-3 text-stone-400" />
        <Input
          placeholder="Search by roll, name or branch…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="students-search"
          className="pl-9 rounded-sm"
        />
      </div>

      <Card className="mt-6 rounded-sm border-stone-200 shadow-none overflow-hidden">
        {loading && <p className="p-6 text-stone-500">Loading…</p>}
        {!loading && (
          <div className="overflow-auto max-h-[70vh]">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b border-stone-200 sticky top-0 z-10">
                <tr className="text-xs uppercase tracking-[0.15em] text-stone-500">
                  <th className="text-left p-4">Roll No</th>
                  <th className="text-left p-4">Name</th>
                  <th className="text-left p-4">Branch</th>
                  <th className="text-left p-4">Batch</th>
                  <th className="text-right p-4">Quick GS Export</th>
                </tr>
              </thead>
              <tbody className="font-mono">
                {filtered.length === 0 && (
                  <tr>
                    <td
                      colSpan={5}
                      className="p-8 text-center text-stone-500 italic"
                    >
                      No students found.
                    </td>
                  </tr>
                )}
                {filtered.map((s) => (
                  <tr
                    key={s.roll_no}
                    className="border-t border-stone-100 hover:bg-stone-50"
                  >
                    <td className="p-4">{s.roll_no}</td>
                    <td className="p-4 sans font-semibold">{s.name}</td>
                    <td className="p-4 text-stone-600 text-xs">{s.branch}</td>
                    <td className="p-4">{s.batch}</td>
                    <td className="p-4 flex justify-end">
                      <DownloadPopover rollNo={s.roll_no} semesters={ALL_SEMESTERS} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
